<script lang="ts">
  import { SELECTED_BUYER_URL_PARAM_KEY, selectedBuyer } from "../../stores/buyer";
  import { push, querystring, location, replace } from "svelte-spa-router";
  import { track } from "../../helpers/mixpanel";
  import { createGetLandingQuery } from "../../helpers/landing";
  import type { Buyer, SubscriptionStatus } from "../../helpers/types";
  import ChevronRight from "../icons/ChevronRight.svelte";
  import Tooltip from "../Tooltip.svelte";
  import { CHAT_FILTER_PARAM_KEY } from "../invoices/constants";
  import TextNew from "../TextNew.svelte";
  import { derived } from "svelte/store";

  export let isPanelExpanded: boolean;

  const landingQuery = createGetLandingQuery();

  let showSelector = false;
  const buyerOptions = derived([landingQuery, selectedBuyer], ([$landingQuery, $selectedBuyer]) =>
    createBuyerOptionsList($landingQuery?.data?.buyers, $selectedBuyer?.id)
  );

  const SUBSCIPTION_STATUS_MAP: Record<SubscriptionStatus, string | null> = {
    active: "active subscription",
    trialing: "trialing subscription",
    canceled: "expired subscription",
    past_due: "expired subscription",
    unpaid: "expired subscription",
    precreated: null,
    incomplete_expired: "expired subscription",
    incomplete: "incomplete subscription",
  };

  function toggleSelector() {
    showSelector = !showSelector;
    track("nav_site_selector_click");
  }

  function hideSelector() {
    showSelector = false;
  }

  $: queryParams = new URLSearchParams($querystring);

  $: if ($selectedBuyer && !queryParams.has(SELECTED_BUYER_URL_PARAM_KEY)) {
    queryParams.set(SELECTED_BUYER_URL_PARAM_KEY, $selectedBuyer.id);
    replace(`${$location}?${queryParams.toString()}`);
  }

  $: if (queryParams.has(SELECTED_BUYER_URL_PARAM_KEY)) {
    const buyerID = queryParams.get(SELECTED_BUYER_URL_PARAM_KEY);
    if (buyerID) {
      syncSelectedBuyerfromURL(buyerID, $landingQuery?.data?.buyers);
    }
  }

  $: if (!$selectedBuyer && $buyerOptions?.length) {
    const buyerWithMostProLines = $buyerOptions.reduce(
      (prev, next) => (prev.pro_count > next.pro_count ? prev : next),
      $buyerOptions[0]
    );
    selectedBuyer.set(buyerWithMostProLines);
  }

  function createBuyerOptionsList(
    buyers: Buyer[] | undefined,
    selectedBuyerID: string | undefined
  ) {
    return buyers
      ?.filter((b) => b.id !== selectedBuyerID)
      .sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });
  }

  function selectBuyer(buyer: Buyer) {
    queryParams.set(SELECTED_BUYER_URL_PARAM_KEY, buyer.id);
    queryParams.delete(CHAT_FILTER_PARAM_KEY);
    push(`${$location}?${queryParams.toString()}`);
    showSelector = false;

    track("overview_select_buyer", {
      selected_buyer_id: buyer.id,
      selected_buyer_name: buyer.name,
    });
  }

  function syncSelectedBuyerfromURL(buyerID: string, buyers: Buyer[] | undefined) {
    const buyer = buyers?.find((b) => b.id === buyerID);
    if (buyer) {
      selectedBuyer.set(buyer);
    }
  }
  function getSubscriptionStatus(buyer: Buyer) {
    if (!buyer.subscription_status) return null;
    return SUBSCIPTION_STATUS_MAP[buyer.subscription_status];
  }

  function getSubscriptionStatusColor(buyer: Buyer) {
    switch (buyer.subscription_status) {
      case "active":
        return "var(--rk-text-low)";
      case "canceled":
      case "past_due":
      case "unpaid":
      case "incomplete_expired":
        return "var(--rk-text-error)";
      default:
        return "";
    }
  }
</script>

<div class="container">
  {#if $selectedBuyer}
    <button on:click={toggleSelector}>
      <div class="active">
        <div class="icon-container"><p class="icon">{$selectedBuyer.name.charAt(0) ?? ""}</p></div>
        {#if isPanelExpanded}
          <div class="name">
            <p>{$selectedBuyer.name}</p>
          </div>
          {#if $buyerOptions && $buyerOptions.length > 1}
            <div class="chevron">
              <ChevronRight size={40} />
            </div>
          {/if}
        {/if}
      </div>
    </button>

    <Tooltip show={showSelector} position="right-top" hide={hideSelector} margin="0 0 0 8px">
      <div class="selector">
        {#if $buyerOptions}
          {#each $buyerOptions as b}
            <button
              class="option"
              on:click={() => {
                selectBuyer(b);
              }}
            >
              <TextNew size="s">{b.name}</TextNew>
              {#if getSubscriptionStatus(b)}
                <TextNew size="xs" --rk-text-color={getSubscriptionStatusColor(b)}
                  >{getSubscriptionStatus(b)}</TextNew
                >
              {/if}
            </button>
          {/each}
        {/if}
      </div>
    </Tooltip>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: var(--rekki-neutral-00);
    border-radius: 8px;
    padding: 9px 8px;
    position: relative;
  }

  .selector {
    width: 264px;
    max-height: 70vh;
    overflow-y: auto;
    padding: 4px;
  }

  .option {
    padding: 9px 12px;
    color: var(--rekki-neutral-04);
    display: block;
    font-size: 16px;
    line-height: 20px;
    width: 100%;
    border-radius: 8px;
  }

  .option:hover {
    background-color: var(--rekki-neutral-01);
    color: var(--rekki-dark);
  }

  button {
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    text-align: left;
    width: 100%;
  }

  .active {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .chevron {
    color: var(--rekki-neutral-02);
  }

  .icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    text-align: center;
    border-radius: 40px;
    background-color: var(--rekki-neutral-02);
    color: var(--rekki-neutral-00);
    margin: 0;
  }

  .icon {
    display: flex;
    justify-content: center;
    padding-bottom: 1px; /* this font... */
    text-align: center;
    font-size: 28px;
  }
  .name {
    margin-left: 8px;
    flex-grow: 1;
    /* overflow: hidden; */
  }
  .name p {
    display: block;
    font-size: 16px;
    margin: 0;
    color: var(--rekki-neutral-04);
    line-height: 20px;
    overflow: hidden;
    position: relative;
    max-height: calc(2 * 20px);
    /* box-sizing: border-box; */
    /* word-break: break-all; */
    /* margin-right: -1em; */
    padding-right: 1em;
  }
  .name p:before {
    content: "...";
    position: absolute;
    right: 0;
    bottom: 0;
  }
  .name p:after {
    content: "";
    position: absolute;
    right: 0;
    width: 1em;
    height: 1em;
    margin-top: 0.2em;
    background-color: var(--rekki-neutral-00);
  }
</style>
